import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout, {
  AuthCard,
  CornerLogo,
  GoogleButton,
  OrDivider,
  AuthInput,
  PrimaryButton,
  AuthLink,
  AUTH,
} from './AuthLayout';

interface Props {
  onLogin: (email: string, password: string) => Promise<void>;
}

export default function LoginPage({ onLogin }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }
    setLoading(true);
    try {
      await onLogin(email.trim(), password.trim());
      const redirect = searchParams.get('redirect');
      navigate(redirect && redirect.startsWith('/') ? redirect : '/app');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      if (msg === 'INVALID_CREDENTIALS' || msg === 'USER_NOT_FOUND') {
        setError('Invalid email or password');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    window.location.href = '/api/auth/google';
  };

  return (
    <AuthLayout>
      <AuthCard>
        {/* Header */}
        <div
          style={{
            padding: '32px 40px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <CornerLogo />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: AUTH.textPrimary,
                lineHeight: '28px',
                textAlign: 'center',
              }}
            >
              Welcome back.
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                fontWeight: 400,
                color: AUTH.textMuted,
                textAlign: 'center',
                lineHeight: '18px',
              }}
            >
              Sign in to pick up where you left off.
            </p>
          </div>
        </div>

        {/* Divider line */}
        <div style={{ height: 1, backgroundColor: AUTH.sectionBorder }} />

        {/* Form area */}
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div
          style={{
            padding: '28px 40px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <GoogleButton onClick={handleGoogle} height={44} radius={10} />
          <OrDivider />

          <AuthInput
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={setEmail}
            height={42}
            radius={9}
          />
          <AuthInput
            label="Password"
            type="password"
            placeholder=""
            value={password}
            onChange={setPassword}
            height={42}
            radius={9}
            rightElement={
              <AuthLink
                onClick={() => navigate('/auth/forgot-password')}
                size={12}
                weight={400}
              >
                Forgot password?
              </AuthLink>
            }
          />

          {error && (
            <p style={{ margin: 0, fontSize: 12, color: AUTH.textError, textAlign: 'center' }}>
              {error}
            </p>
          )}

          <PrimaryButton
            disabled={loading}
            type="submit"
            style={{
              height: 46,
              borderRadius: 10,
              background: loading ? '#D9C4B0' : AUTH.accent,
              marginTop: 4,
            }}
          >
            {loading ? 'Signing in\u2026' : 'Sign in \u2192'}
          </PrimaryButton>
        </div>
        </form>

        {/* Footer */}
        <div
          style={{
            padding: '4px 32px 28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <p style={{ margin: 0, fontSize: 12, color: AUTH.textSecondary, lineHeight: '16px' }}>
            Don&apos;t have an account?{' '}
            <AuthLink onClick={() => {
              const redirect = searchParams.get('redirect');
              navigate(redirect ? `/auth/register?redirect=${encodeURIComponent(redirect)}` : '/auth/register');
            }}>Create one</AuthLink>
          </p>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
